import { c as _c } from "react/compiler-runtime";
import { translateInline } from '@/globals/translate/translateInline';
import { produce } from '@/globals/helpers/immer';
import { useCallback, useId, useState } from 'react';
import { Form, Fieldset, QuestionTitle, StackAnswers, AnswerContainer, Checkbox, AnswerLabel, FormError, GetHasUnansweredQuestionsContext, GetHasUnansweredQuestions, QuestionSubtitle } from '../currentQuestion/CurrentQuestionParts';
import { classes } from '@/uiPrimitives/base/classes.helpers';
import { stack } from '@/uiPrimitives/layout/stack';
import { AnyRenderedQuestion, GivenAnswers } from '../question.types';
import { MultipleSelectGivenAnswer, RenderedMultipleSelectQuestion } from './multipleSelectMethods.types';
import { AnswerHelp, AnswerHelpButton, QuestionHelp } from '../currentQuestion/Help';
import { getHasUnansweredQuestions } from '../../_data/filterQuestions';
import { QuestionMethods } from '../questionMethods.types';
import { QuestionNumber } from '../AnsweredQuestionParts';
const getHasSomePossibleUnansweredQuestions = ({
  question,
  givenAnswers,
  allQuestions
}: {
  question: RenderedMultipleSelectQuestion;
  givenAnswers: GivenAnswers;
  allQuestions: Array<AnyRenderedQuestion>;
}) => {
  const givenAnswersWithAllCurrentAnswers = produce(givenAnswers, givenAnswers => {
    givenAnswers.multiple.set(question.id, new Set(question.answers.map(({
      id
    }) => id)));
  });
  const hasSomePossibleUnansweredQuestions = getHasUnansweredQuestions(allQuestions, givenAnswersWithAllCurrentAnswers);
  return hasSomePossibleUnansweredQuestions;
};
const getMultipleHasUnansweredQuestions = ({
  allQuestions,
  givenAnswers,
  question,
  currentGivenAnswer
}: {
  allQuestions: Array<AnyRenderedQuestion>;
  givenAnswers: GivenAnswers;
  question: RenderedMultipleSelectQuestion;
  currentGivenAnswer: MultipleSelectGivenAnswer;
}) => {
  const hasSomePossibleUnansweredQuestions = getHasSomePossibleUnansweredQuestions({
    question,
    givenAnswers,
    allQuestions
  });
  const speculativeGivenAnswers = produce(givenAnswers, givenAnswers => {
    givenAnswers.multiple.set(question.id, currentGivenAnswer);
  });
  const hasUnansweredQuestions = getHasUnansweredQuestions(allQuestions, speculativeGivenAnswers);
  if (!hasSomePossibleUnansweredQuestions) {
    return hasSomePossibleUnansweredQuestions;
  }
  return hasUnansweredQuestions;
};
export const CurrentQuestionMultiple: QuestionMethods<RenderedMultipleSelectQuestion, MultipleSelectGivenAnswer>['Current'] = t0 => {
  const $ = _c(59);
  const {
    question,
    givenAnswers,
    onSubmit,
    containerId,
    titleId,
    progressSlot,
    buttonsSlot
  } = t0;
  let t1;
  if ($[0] !== givenAnswers.multiple || $[1] !== question.id) {
    t1 = givenAnswers.multiple.get(question.id) || new Set();
    $[0] = givenAnswers.multiple;
    $[1] = question.id;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const givenAnswer = t1;
  const errorId = useId();
  const [currentGivenAnswer, setCurrentGivenAnswer] = useState(givenAnswer);
  let t2;
  if ($[3] !== currentGivenAnswer || $[4] !== question) {
    t2 = t3 => {
      const {
        allQuestions,
        givenAnswers: givenAnswers_0
      } = t3;
      return getMultipleHasUnansweredQuestions({
        allQuestions,
        givenAnswers: givenAnswers_0,
        question,
        currentGivenAnswer
      });
    };
    $[3] = currentGivenAnswer;
    $[4] = question;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const getHasUnansweredQuestions = t2;
  let t3;
  if ($[6] !== currentGivenAnswer || $[7] !== givenAnswer || $[8] !== onSubmit || $[9] !== question.answers || $[10] !== question.id) {
    t3 = () => {
      onSubmit({
        givenAnswersProducer: draft => {
          draft.multiple.set(question.id, currentGivenAnswer);
        },
        getAnswerTitle: () => question.answers.filter(answer => givenAnswer.has(answer.id)).map(_temp).join(";")
      });
    };
    $[6] = currentGivenAnswer;
    $[7] = givenAnswer;
    $[8] = onSubmit;
    $[9] = question.answers;
    $[10] = question.id;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  let t5;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = classes(stack({
      rowGap: "3 | h1"
    }));
    t5 = <QuestionNumber />;
    $[12] = t4;
    $[13] = t5;
  } else {
    t4 = $[12];
    t5 = $[13];
  }
  let t6;
  if ($[14] !== question.title || $[15] !== titleId) {
    t6 = <QuestionTitle id={titleId}>{question.title}</QuestionTitle>;
    $[14] = question.title;
    $[15] = titleId;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== question.help || $[18] !== question.id || $[19] !== question.titleText || $[20] !== t6) {
    t7 = <QuestionHelp help={question.help} titleText={question.titleText} questionId={question.id}>{t6}</QuestionHelp>;
    $[17] = question.help;
    $[18] = question.id;
    $[19] = question.titleText;
    $[20] = t6;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[22] !== question.subtitle) {
    t8 = Boolean(question.subtitle) && <QuestionSubtitle>{question.subtitle}</QuestionSubtitle>;
    $[22] = question.subtitle;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  let t9;
  if ($[24] !== currentGivenAnswer || $[25] !== errorId || $[26] !== question.answers || $[27] !== question.id || $[28] !== question.optional || $[29] !== question.titleText) {
    let t10;
    if ($[31] !== currentGivenAnswer || $[32] !== errorId || $[33] !== question.id || $[34] !== question.optional || $[35] !== question.titleText) {
      t10 = answer_1 => {
        const isChecked = currentGivenAnswer.has(answer_1.id);
        return <AnswerHelp help={answer_1.help} key={answer_1.id}><AnswerContainer><Checkbox name={answer_1.id} checked={isChecked} required={!question.optional && (isChecked || currentGivenAnswer.size === 0)} onChange={t11 => {
              const {
                currentTarget
              } = t11;
              const {
                checked
              } = currentTarget;
              setCurrentGivenAnswer(produce(currentGivenAnswerDraft => {
                if (checked) {
                  currentGivenAnswerDraft.add(answer_1.id);
                } else {
                  currentGivenAnswerDraft.delete(answer_1.id);
                }
              }));
            }} aria-describedby={errorId} /><AnswerLabel helpSlot={Boolean(answer_1.help) && <AnswerHelpButton titleText={answer_1.titleText} questionTitle={question.titleText} currentQuestionId={question.id} />}>{answer_1.title}</AnswerLabel></AnswerContainer></AnswerHelp>;
      };
      $[31] = currentGivenAnswer;
      $[32] = errorId;
      $[33] = question.id;
      $[34] = question.optional;
      $[35] = question.titleText;
      $[36] = t10;
    } else {
      t10 = $[36];
    }
    t9 = question.answers.map(t10);
    $[24] = currentGivenAnswer;
    $[25] = errorId;
    $[26] = question.answers;
    $[27] = question.id;
    $[28] = question.optional;
    $[29] = question.titleText;
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  let t10;
  if ($[37] !== t9) {
    t10 = <StackAnswers>{t9}</StackAnswers>;
    $[37] = t9;
    $[38] = t10;
  } else {
    t10 = $[38];
  }
  let t11;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = translateInline({
      en: "Please select at least one answer",
      nl: "Selecteer minstens \xE9\xE9n antwoord"
    });
    $[39] = t11;
  } else {
    t11 = $[39];
  }
  let t12;
  if ($[40] !== errorId) {
    t12 = <FormError id={errorId}>{t11}</FormError>;
    $[40] = errorId;
    $[41] = t12;
  } else {
    t12 = $[41];
  }
  let t13;
  if ($[42] !== t10 || $[43] !== t12 || $[44] !== t7 || $[45] !== t8 || $[46] !== titleId) {
    t13 = <Fieldset aria-labelledby={titleId}>{t7}{t8}{t10}{t12}</Fieldset>;
    $[42] = t10;
    $[43] = t12;
    $[44] = t7;
    $[45] = t8;
    $[46] = titleId;
    $[47] = t13;
  } else {
    t13 = $[47];
  }
  let t14;
  if ($[48] !== progressSlot || $[49] !== t13) {
    t14 = <div className={t4}>{t5}{progressSlot}{t13}</div>;
    $[48] = progressSlot;
    $[49] = t13;
    $[50] = t14;
  } else {
    t14 = $[50];
  }
  let t15;
  if ($[51] !== buttonsSlot || $[52] !== containerId || $[53] !== t14 || $[54] !== t3) {
    t15 = <Form id={containerId} onSubmit={t3}>{t14}{buttonsSlot}</Form>;
    $[51] = buttonsSlot;
    $[52] = containerId;
    $[53] = t14;
    $[54] = t3;
    $[55] = t15;
  } else {
    t15 = $[55];
  }
  let t16;
  if ($[56] !== getHasUnansweredQuestions || $[57] !== t15) {
    t16 = <GetHasUnansweredQuestionsContext.Provider value={getHasUnansweredQuestions}>{t15}</GetHasUnansweredQuestionsContext.Provider>;
    $[56] = getHasUnansweredQuestions;
    $[57] = t15;
    $[58] = t16;
  } else {
    t16 = $[58];
  }
  return t16;
};
function _temp(answer_0) {
  return answer_0.titleText;
}